import { useCallback, useState } from 'react';
import { isDesktop } from 'react-device-detect';

import { useMutation } from '@tanstack/react-query';

import classNames from 'classnames';

import { Dropdown, DropdownItem, useModalController } from '@uikit';

import type { DeserializedAppData} from '@api';
import { sendReport, sendTrustReport } from '@api';

import { FlagOutlined } from '@uikit/icons/FlagOutlined';

import type { ReviewContentReviewData } from '@shared/api/types/review';
import { REVIEW_TYPE } from '@shared/api/types/review';
import { useAuth } from '@shared/common/providers/AuthProvider';
import { useAuthModal } from '@shared/common/providers/GlobalModalsProvider';
import displayErrorToast from '@shared/helpers/displayErrorToast';
import { useFeatureToggles } from '@shared/hooks/useFeatureToggles';

import { useReviewReportTypesQuery } from '@query/review';

import type {
  ReportModalProps,
} from '@store:web/components/common/modals/ReportModal/ReportModal';
import ReportModal from '@store:web/components/common/modals/ReportModal/ReportModal';
import ShareModal from '@store:web/components/common/modals/ShareModal';
import useReviewShareConfig from '@store:web/components/common/ReviewFlag/useReviewShareConfig';

enum ModalHeading {
  report = 'Report Review',
  trustReport = 'Trust Report Review',
}

export interface ReviewFlagProps {
  review: ReviewContentReviewData;
  app: Pick<DeserializedAppData['attributes'], 'appLink' | 'appName'>;
}

export default function ReviewFlag({ review, app }: ReviewFlagProps) {
  const { isAuth, isLoading } = useAuth();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const loginModal = useAuthModal();
  const shareModal = useModalController();
  const reportModal = useModalController();
  const { reviewFlagShare, reviewFlagReport } = useFeatureToggles();
  const { shareUrl, shareConfig } = useReviewShareConfig({
    review,
    app,
  });
  const {
    data: reviewReportTypes,
    isFetching: isLoadingReportTypes,
    refetch,
  } = useReviewReportTypesQuery({ enabled: false });
  const {
    mutateAsync: report,
    isPending: isReporting,
    isSuccess: wasReportSent,
    reset: resetMutationState,
  } = useMutation({ mutationFn: sendReport });

  const {
    mutateAsync: reportTrust,
    isPending: isTrustReporting,
    isSuccess: hasTrustReportSent,
  } = useMutation({ mutationFn: sendTrustReport });

  const handleReportClick = () => {
    try {
      refetch();
      reportModal.open();
    } catch (error) {
      displayErrorToast({ error });
    }
  };

  const reportCallback: ReportModalProps['reportCallback'] = useCallback(
    async ({ comment, reportType }) => {
      await report({
        json: {
          appLink: app.appLink,
          reviewId: review.id,
          reportText: comment,
          reportType: reportType.id,
        },
      });
    },
    [report, app.appLink, review.id],
  );

  const reportTrustCallback: ReportModalProps['reportCallback'] = useCallback(
    async ({ comment, reportType }) => {
      await reportTrust({
        json: {
          appLink: app.appLink,
          reviewId: review.id,
          reportText: comment,
          reportType: reportType.id,
        },
      });
    },
    [reportTrust, app.appLink, review.id],
  );

  if (!isAuth) {
    return (
      <button
        onClick={(event) => {
          event.stopPropagation();

          if (!isLoading) {
            loginModal.open();
          }
        }}
        className={classNames(
          'text-primary-600 w-6 h-6 p-[6px] flex-shrink-0 rounded-full flex items-center justify-center transition duration-300',
          isLoading ? 'cursor-not-allowed' : 'cursor-pointer hover:bg-primary-100',
        )}
      >
        <FlagOutlined />
      </button>
    );
  }

  const dropdownProps = isDesktop
    ? { showOnHover: true }
    : {
        isOpen: isDropdownOpen,
        onClose: () => setIsDropdownOpen(false),
      };

  const isTrustReview = review.type === REVIEW_TYPE.TRUST_REVIEW;
  const modalHeading = isTrustReview ? ModalHeading.trustReport : ModalHeading.report;
  const callback = isTrustReview ? reportTrustCallback : reportCallback;

  return (
    <>
      <Dropdown
        {...dropdownProps}
        placement="bottom-end"
        trigger={
          <div
            onClick={(event) => {
              event.stopPropagation();

              if (!dropdownProps.showOnHover) {
                setIsDropdownOpen(true);
              }
            }}
            className="text-primary-600 w-6 h-6 p-[6px] flex-shrink-0 rounded-full flex items-center justify-center hover:bg-primary-100 transition duration-300 cursor-pointer"
          >
            <FlagOutlined />
          </div>
        }
        onClick={(event) => event.stopPropagation()}
      >
        <DropdownItem
          label="Share Review"
          className={classNames('flex gap-2.5 items-center', {
            'cursor-not-allowed bg-neutral-100 hover:bg-neutral-100 opacity-40': !reviewFlagShare,
          })}
          onClick={!reviewFlagShare ? undefined : shareModal.open}
          disabled={!reviewFlagShare}
        >
          Share Review
        </DropdownItem>

        <DropdownItem
          label="Report Review"
          className={classNames('flex gap-2.5 items-center', {
            'cursor-not-allowed bg-neutral-100 hover:bg-neutral-100 opacity-40': !reviewFlagReport,
          })}
          onClick={!reviewFlagReport ? undefined : handleReportClick}
          disabled={isLoadingReportTypes || !reviewFlagReport}
        >
          Report Review
        </DropdownItem>
      </Dropdown>

      <ShareModal
        url={shareUrl}
        modalHeading="Share Review"
        platformConfigurations={shareConfig}
        modalController={shareModal}
      />

      <ReportModal
        isReporting={isReporting || isTrustReporting}
        isLoadingReportTypes={isLoadingReportTypes}
        isSuccess={wasReportSent || hasTrustReportSent}
        modalController={reportModal}
        modalHeading={modalHeading}
        reportCallback={callback}
        reportTypes={reviewReportTypes || []}
        resetStates={resetMutationState}
      />
    </>
  );
}
